import { Stack } from '@mui/material';

import { useState, useEffect, useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';

import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import {
	AI_TYPE,
	AI_SIMULATION_TYPES,
	AI_SIMULATION_MODEL_KEYS,
} from 'common/constants/AISimulationConstants';
import { RootState } from 'common/store/AppStore';
import {
	setSimulationCommonShareDrawer,
	setToastMessage,
} from 'common/store/reducer/commonReducer';
import { setControlStepsData } from 'common/store/reducer/controlTestScriptReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { genControlTestScript, generateControlTestPromptResult } from '../api';
import { IAIModelDetails } from '../api/type-def';
import { aiModelPDFDownload } from '../Components/AIModelPDFDownload/AIModelPDFDownload';
import AIModelSave from '../Components/AIModelSave/AIModelSave';
import AIShare from '../Components/AIShare/AIShare';
import GeneratedTextArea from '../Components/GeneratedTextArea/GeneratedTextArea';
import generatedTextAreaStyles from '../Components/GeneratedTextArea/GeneratedTextArea.module.scss';
import StepHeader from '../Components/StepHeader/StepHeader';
import useInputOutput from '../hooks/useInputOutput';

import ControlTestScriptOutputLayout from './ControlTestScriptOutputLayout';

const { CONTROL_TEST_SCRIPTS, CONTROL_TEST_SCRIPTS_BUTTON } =
	AI_SIMULATION_TYPES;
const { CONTROL_TEST_SCRIPTS_KEY } = AI_SIMULATION_MODEL_KEYS;

const ControlTestScriptsPage = () => {
	const {
		inputTxt,
		updateInput,
		outputResult,
		handleGenerate,
		isGenerating,
		updateOutput,
		handlePromptSelect,
	} = useInputOutput({
		aiModelKey: CONTROL_TEST_SCRIPTS_KEY,
		workflowStep: CONTROL_TEST_SCRIPTS,
		requestFn: genControlTestScript,
		reqPromptSelect: generateControlTestPromptResult,
	});

	const selectedAIModel = useSelector(
		(state: RootState) => state?.simulationArena?.selectedAIModel,
	);

	const dispatch = useDispatch();
	const { MODEL } = AI_TYPE;

	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({
				isToastOpen: false,
				message: '',
				severity: '',
			}),
		);
	}, []);

	useEffect(() => {
		if (selectedAIModel.input && selectedAIModel.output) {
			updateInput(selectedAIModel.input as string);
			updateOutput(selectedAIModel.output);
		}
	}, [selectedAIModel]);

	const outputActionClassName = outputResult
		? ''
		: generatedTextAreaStyles.disabled;

	const [testScriptSteps, setTestScriptSteps] = useState<any>();
	const controlStepsList = useSelector(
		(state: RootState) => state.controlTestScript.controlStepsList,
	);

	useEffect(() => {
		const textOnly = outputResult.split(/\d{1,5}\.\s/).filter(Boolean);
		setTestScriptSteps(textOnly);
		dispatch(setControlStepsData(textOnly));
	}, [outputResult]);

	const generatedOutputText = () => {
		const target = _.isEmpty(controlStepsList)
			? testScriptSteps
			: controlStepsList;
		const arrWithoutNewLine = (target as Array<any>).map((item, index) => {
			if (index !== target.length - 1) {
				return `${index + 1}. ${item.substring(0, item.length - 1)}`;
			}
			return `${index + 1}. ${item}`;
		});
		return arrWithoutNewLine.join('\n');
	};

	const aiModelData: IAIModelDetails = {
		...(selectedAIModel?.id ? { id: selectedAIModel.id } : {}),
		name: selectedAIModel?.name || '',
		input: inputTxt,
		output: testScriptSteps && generatedOutputText(),
		workflowStep: CONTROL_TEST_SCRIPTS,
		type: MODEL,
	};
	const fileDownload = () => {
		aiModelPDFDownload(
			_.isEmpty(selectedAIModel) ? null : selectedAIModel.name,
			CONTROL_TEST_SCRIPTS,
			CONTROL_TEST_SCRIPTS,
			{
				inputTitle: 'Insert Control Statement',
				inputValue: inputTxt,
			},
			{
				outputTitle: CONTROL_TEST_SCRIPTS,
				outputValue: testScriptSteps && generatedOutputText(),
			},
		);
	};

	const handleShare = () => {
		dispatch(setSimulationCommonShareDrawer(true));
	};

	return (
		<>
			<Stack>
				<StepHeader title={CONTROL_TEST_SCRIPTS} />

				<Stack gap={3}>
					<GeneratedTextArea
						category={MODEL}
						title={toSentenceCase('Insert Control Statement')}
						onChange={updateInput}
						button={{
							text: `${
								outputResult ? 'Regenerate' : 'Generate'
							} ${CONTROL_TEST_SCRIPTS_BUTTON}`,
							aiModelKey: CONTROL_TEST_SCRIPTS_KEY,
							type: outputResult ? 'tertiary' : 'primary',
							loading: isGenerating,
							onClick: handleGenerate,
							onPromptChange: handlePromptSelect,
						}}
						textValue={inputTxt}
					/>

					{outputResult && (
						<ControlTestScriptOutputLayout
							category={MODEL}
							inputData={inputTxt}
							outputResult={outputResult}
							testScriptSteps={testScriptSteps}
							outputActionClassName={outputActionClassName}
							downloadHandler={fileDownload}
							shareHandler={handleShare}
							isGenerating={isGenerating}
						/>
					)}
				</Stack>
				{outputResult && <AIModelSave aiModeldata={aiModelData} />}
			</Stack>
			<AIShare aiSharedData={aiModelData} type={MODEL} />
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
				data-testid="alert-component"
			/>
		</>
	);
};

export default ControlTestScriptsPage;
